import React, {
  createContext,
  PropsWithChildren,
  useState,
  useCallback,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react'

import type { Advisor, CustomerSortOptions } from '../../../../../graphql/api/types'

type HeaderData = {
  sort: CustomerSortOptions
  search: string
  advisor: Advisor | null
}

type HeaderSetters = {
  setSort: (options: CustomerSortOptions) => void
  setSearch: (search: string) => void
  setAdvisor: (advisor: Advisor | null) => void
}

type Header = {
  data: HeaderData
  setters: HeaderSetters
}

type ListClientsErrorState = {
  value: Error | null
  refetch: () => Promise<unknown>
}

type ListClientsContextType = {
  header: Header
  error: ListClientsErrorState & {
    set: (errorState: ListClientsErrorState) => void
  }
  setSelectedClientId: Dispatch<SetStateAction<string>>
  selectedClientId: string
}

const INITIAL_DATA: ListClientsContextType = {
  header: {
    data: {
      advisor: null,
      search: '',
      sort: (localStorage.getItem('CLIENT_PREFERRED_SORT_OPTION') as CustomerSortOptions) || 'MOST_RECENT',
    },
    setters: {
      setSort: () => undefined,
      setSearch: () => undefined,
      setAdvisor: () => undefined,
    },
  },
  error: {
    value: null,
    refetch: async () => undefined,
    set: () => undefined,
  },
  setSelectedClientId: () => undefined,
  selectedClientId: '',
}

export const ListClientsContext = createContext<ListClientsContextType>(INITIAL_DATA)

export const ListClientsProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [selectedClientId, setSelectedClientId] = useState('')
  const [headerData, setHeaderData] = useState<HeaderData>(INITIAL_DATA.header.data)
  const [errorState, setErrorState] = useState<ListClientsErrorState>({
    value: null,
    refetch: async () => undefined,
  })

  const setSort = useCallback((option: CustomerSortOptions): void => {
    setHeaderData((prev) => ({ ...prev, sort: option }))
  }, [])

  const setSearch = useCallback(
    (search: string): void => {
      if (search !== headerData.search) {
        setHeaderData((prev) => ({ ...prev, search }))
      }
    },
    [headerData.search],
  )

  const setAdvisor = useCallback((advisor: Advisor | null): void => {
    setHeaderData((prev) => ({ ...prev, advisor }))
  }, [])

  const setContextError = useCallback((error: ListClientsErrorState) => {
    setErrorState(error)
  }, [])

  const contextValue = useMemo(
    () => ({
      header: {
        data: headerData,
        setters: {
          setSort,
          setSearch,
          setAdvisor,
        },
      },
      error: {
        value: errorState.value,
        refetch: errorState.refetch,
        set: setContextError,
      },
      setSelectedClientId: setSelectedClientId,
      selectedClientId: selectedClientId,
    }),
    [headerData, setSort, setSearch, setAdvisor, errorState, setContextError, selectedClientId, setSelectedClientId],
  )

  return <ListClientsContext.Provider value={contextValue}>{children}</ListClientsContext.Provider>
}
